<script setup lang="ts">
import { computed } from 'vue'
import { 
  Calendar,
  Ticket,
  FolderKanban,
  CheckSquare,
  Mail
} from 'lucide-vue-next'

interface ScheduledEvent {
  id: number
  tech_id: number
  start_time: string
  end_time: string
  linked_type: 'ticket' | 'project_task' | 'todo' | 'outlook_external'
  linked_id: number
  linked_title: string
  notes: string
  source?: 'hq' | 'outlook'
}

interface Props {
  events: ScheduledEvent[]
  weekStart: Date
  compact?: boolean
  showTechName?: boolean
  techName?: string
}

const props = withDefaults(defineProps<Props>(), {
  compact: false,
  showTechName: false,
  techName: ''
})

const weekDays = computed(() => {
  const days = []
  for (let i = 0; i < 7; i++) {
    const d = new Date(props.weekStart)
    d.setDate(d.getDate() + i)
    days.push({
      date: d,
      dayName: d.toLocaleDateString('en-US', { weekday: 'short' }),
      dayNum: d.getDate(),
      month: d.toLocaleDateString('en-US', { month: 'short' }),
      isToday: isSameDay(d, new Date())
    })
  }
  return days
})

function isSameDay(d1: Date, d2: Date): boolean {
  return d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
}

function getEventsForDay(day: Date): ScheduledEvent[] {
  return props.events.filter(e => {
    const eventDate = new Date(e.start_time)
    return isSameDay(eventDate, day)
  })
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  })
}

function getEventColor(type: string): string {
  switch (type) {
    case 'ticket': return 'bg-primary-500/20 border-primary-500 text-primary-300'
    case 'project_task': return 'bg-violet-500/20 border-violet-500 text-violet-300'
    case 'todo': return 'bg-teal-500/20 border-teal-500 text-teal-300'
    case 'outlook_external': return 'bg-orange-500/20 border-orange-500 text-orange-300'
    default: return 'bg-surface-600 border-surface-500'
  }
}

function getEventIcon(type: string) {
  switch (type) {
    case 'ticket': return Ticket
    case 'project_task': return FolderKanban
    case 'todo': return CheckSquare
    case 'outlook_external': return Mail
    default: return Calendar
  }
}
</script>

<template>
  <div class="border border-surface-700 rounded-lg overflow-hidden">
    <!-- Header Row - Days -->
    <div class="grid grid-cols-7 border-b border-surface-700">
      <div 
        v-for="day in weekDays" 
        :key="day.date.toISOString()" 
        class="p-2 text-center border-r border-surface-700 last:border-r-0"
        :class="[
          day.isToday ? 'bg-primary-500/10' : 'bg-surface-800',
          compact ? 'py-1' : 'py-2'
        ]"
      >
        <div class="text-xs text-surface-400">{{ day.dayName }}</div>
        <div 
          class="font-semibold" 
          :class="[
            day.isToday ? 'text-primary-400' : 'text-surface-100',
            compact ? 'text-sm' : 'text-lg'
          ]"
        >
          {{ day.dayNum }}
        </div>
        <div v-if="!compact" class="text-xs text-surface-500">{{ day.month }}</div>
      </div>
    </div>

    <!-- Tech Name Row (optional) -->
    <div v-if="showTechName && techName" class="px-3 py-1.5 bg-surface-800/50 border-b border-surface-700">
      <span class="text-xs font-medium text-surface-400">{{ techName }}</span>
    </div>

    <!-- Events Row -->
    <div class="grid grid-cols-7">
      <div 
        v-for="day in weekDays" 
        :key="`events-${day.date.toISOString()}`"
        class="border-r border-surface-700 last:border-r-0"
        :class="[
          day.isToday ? 'bg-primary-500/5' : '',
          compact ? 'p-1 min-h-[60px]' : 'p-2 min-h-[80px]'
        ]"
      >
        <!-- Events for this day -->
        <div 
          v-for="event in getEventsForDay(day.date)" 
          :key="event.id"
          class="mb-1 p-1.5 rounded border text-xs"
          :class="getEventColor(event.linked_type)"
        >
          <div class="flex items-start gap-1">
            <component :is="getEventIcon(event.linked_type)" class="w-3 h-3 mt-0.5 flex-shrink-0" />
            <div class="flex-1 min-w-0">
              <div class="font-medium truncate" :class="compact ? 'text-[10px]' : 'text-xs'">
                {{ event.linked_title || 'Untitled' }}
              </div>
              <div class="opacity-75" :class="compact ? 'text-[8px]' : 'text-[10px]'">
                {{ formatTime(event.start_time) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Empty state indicator -->
        <div 
          v-if="getEventsForDay(day.date).length === 0" 
          class="h-full flex items-center justify-center"
        >
          <span class="text-[10px] text-surface-600">—</span>
        </div>
      </div>
    </div>
  </div>
</template>
